import { forwardRef } from 'react';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { postfixTestId } from '../../../util/testId';
import { Button, ButtonVariant } from '../../Button';
import { Divider } from '../../Divider';
import { Heading } from '../../Heading';
import { MenuListDesign, useFocusNavigation } from '../../Menu/__internal__';
import { DIRTY_INDICATOR } from '../shared';
import bundle from '../NovaComponents-ObjectManager-gui-icu.properties';
import cn from './ObjectSwitcherPanel.css.json';
import './ObjectSwitcherPanel.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
export const headingId = 'object-switcher-panel-header';

/**
 * The panel used by the ObjectSwitcher
 * @see https://rndconfluence.sas.com/confluence/pages/viewpage.action?spaceKey=NOVA&title=ObjectManager
 */
export const ObjectSwitcherPanel = /*#__PURE__*/forwardRef(function ObjectSwitcherPanel(props, forwardRef) {
  const localize = useTextLocalizer();
  const sections = switcherSectionsToMenuSections(props.sections);
  const selectedItemLocation = getSelectedItemLocation(sections);
  const {
    focusLocation,
    onKeyDown
  } = useFocusNavigation({
    sections,
    isOpen: true,
    defaultFocusLocation: sections.length > 0 ? selectedItemLocation[0] : undefined
  });
  const dt = useDesignTokens();
  const style = renderInlineCssVars({
    'spacing-l': dt.tokens['space-base-l'],
    'spacing-m': dt.tokens['space-base-m'],
    'spacing-xl': dt.tokens['space-base-xl'],
    'font-weight-thick': dt.tokens['font-style-weight-700']
  }, 'object-switcher-panel');
  return /*#__PURE__*/_jsxs("div", {
    ref: forwardRef,
    className: cn.container,
    style: style,
    children: [/*#__PURE__*/_jsx(Heading, {
      id: headingId,
      level: 3,
      className: cn.heading,
      children: localize('objectManager.objectSwitcher.panel.openedItems.title')
    }), /*#__PURE__*/_jsx(MenuListDesign, {
      firstItemRef: props.firstItemRef,
      tabIndex: -1,
      className: cn.list,
      onKeyDown: onKeyDown,
      sections: sections,
      focusLocation: focusLocation,
      disabled: false,
      onItemSelected: itemLocation => {
        props.onObjectChange?.({
          item: lookupViaItemLocation(props.sections, itemLocation)
        });
      },
      onItemClosed: itemLocation => {
        props.onObjectClose?.({
          item: lookupViaItemLocation(props.sections, itemLocation)
        });
      },
      showFocus: props.showFocus ?? true
    }), /*#__PURE__*/_jsx(Divider, {
      className: cn.divider
    }), /*#__PURE__*/_jsx("div", {
      className: cn.buttonContainer,
      children: /*#__PURE__*/_jsx(Button, {
        text: localize('objectManager.objectSwitcher.panel.closeAllItems.button.label'),
        variant: ButtonVariant.Light,
        onPress: props.onObjectCloseAll,
        testId: postfixTestId(props.testId, 'object-switcher-close-all-button')
      })
    })]
  });
});
const switcherSectionsToMenuSections = switcherSections => {
  const menuSections = [];
  const formatTitle = (text, isDirty) => isDirty ? `${DIRTY_INDICATOR}${text}` : text;
  const flattenSubItems = items => {
    const _flattenSubItems = (items, level) => {
      let menuItems = [];
      for (const item of items) {
        menuItems.push({
          ...item,
          text: formatTitle(item.text, item.isDirty),
          indentation: level
        });
        if (Array.isArray(item.items)) {
          menuItems = menuItems.concat(_flattenSubItems(item.items, level + 1));
        }
      }
      return menuItems;
    };
    return _flattenSubItems(items, 1);
  };
  for (const {
    items,
    title
  } of switcherSections) {
    let menuItems = [];
    for (const item of items) {
      menuItems.push({
        ...item,
        text: formatTitle(item.text, item.isDirty)
      });
      if (items) {
        menuItems = menuItems.concat(flattenSubItems(item.items ?? []));
      }
    }
    menuSections.push({
      title,
      items: menuItems.map(menuItem => ({
        closable: true,
        ...menuItem
      }))
    });
  }
  return menuSections;
};
const getSelectedItemLocation = sections => {
  // find and return the location of the first checked item

  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const items = sections[sectionIndex].items;
    for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
      if (items[itemIndex].checked) {
        return [[sectionIndex, itemIndex]];
      }
    }
  }
  return [[0, 0]];
};
const lookupViaItemLocation = (switcherSections, itemLocation) => {
  const lookupSectionViaKey = (key, switcherSections) => {
    const lookupItem = items => {
      for (const currentItem of items) {
        if (currentItem.key === key) return currentItem;
        if (currentItem.items) {
          const foundItem = lookupItem(currentItem.items);
          if (foundItem) return foundItem;
        }
      }
      return null;
    };
    for (const section of switcherSections) {
      const foundItem = lookupItem(section.items);
      if (foundItem) return foundItem;
    }
  };
  const sections = switcherSectionsToMenuSections(switcherSections);
  const [sectionIndex, itemIndex] = itemLocation[0];
  const targetItem = sections[sectionIndex].items[itemIndex];
  return lookupSectionViaKey(targetItem.key, switcherSections);
};
//# sourceMappingURL=ObjectSwitcherPanel.js.map